import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';

import { useUnauthenticatedSignForm } from '../signForm/useUnauthenticatedSignForm';
import { useShareCtaPlacementEnabled } from '../useShareCtaPlacementEnabled';

export type ShareCtaPlacement = 'below' | 'beside' | null;

/**
 * Single source for the sign-CTA share affordance's placement, consumed both by
 * `MobileSignButton` (to render it) and `NativeShareMobile` (to suppress the hero FAB) — see the
 * single-source gate note in the plan's High-Level Technical Design. A second, independently
 * derived gate is how the original two-entry-point bug happened.
 */
export function useShareCtaPlacement(): ShareCtaPlacement {
	const enabled = useShareCtaPlacementEnabled();
	const {
		data: {
			petition: { status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: {
			decisionMaker,
			signed,
			experiments: { petitionDetailsShareCtaPlacementRerunExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();

	// `MobileSignButtonContainer` — the affordance's only render site — mounts under this exact
	// condition (see `MobileActionButtonContainer`'s branch selection); anywhere else
	// (decisionMaker, CLOSED/VICTORY, or an already-signed visitor) it's `MobileRespondButton`,
	// `MobileShareButtonContainer`, or `MobileComboButtonContainer` instead. Suppressing the hero
	// FAB outside that condition would drop the share affordance entirely for those visitors
	// (CHANGE-98647) — the bug this hook's "single source" doc comment exists to prevent.
	const signCtaWillRender =
		!decisionMaker && status !== 'CLOSED' && status !== 'VICTORY' && (!signed || unauthenticatedSignFormEnabled);

	if (!enabled || !signCtaWillRender) {
		return null;
	}

	switch (petitionDetailsShareCtaPlacementRerunExperiment.variation) {
		case 'v1':
			return 'below';
		case 'v2':
			return 'beside';
		default:
			return null;
	}
}
